/**
 * Reading of `status.history` on an ArgoCD Application — what the per-branch
 * page shows as "what was deployed here, and when".
 *
 * Every field is optional, and that is not defensive politeness: the payload
 * reaches us verbatim from the ArgoCD API through a proxy, so a field this code
 * assumes present is a field that can blank the whole page on an ArgoCD
 * upgrade. Nothing here throws; a missing value becomes an absent value.
 */

import { historyProjectSource, type ArgoApplication } from './argoApp';
import { repoForge } from './pipelines';

/** One deployment, flattened to what the history table renders. */
export interface DeployHistoryEntry {
  /** ArgoCD's own incrementing deployment number, when it sent one. */
  id?: number;
  /** Full commit SHA that was deployed. */
  revision?: string;
  /** ISO timestamp of the moment the deployment finished. */
  deployedAt?: string;
  /** Repository the revision belongs to, browsable (no `.git` suffix). */
  repoUrl?: string;
}

/**
 * The deployments of one Application, newest first.
 *
 * The list is *reversed*, not sorted. ArgoCD appends to `status.history`, so
 * what arrives is oldest-first and reversing it preserves exactly the order
 * ArgoCD chose. Sorting on `id` or on `deployedAt` would look more rigorous and
 * be less so: both are optional here, and a comparator keyed on a field half
 * the entries lack reorders the list in a way nobody can predict from the
 * screen.
 *
 * The input array is copied before being reversed — the argument is the parsed
 * API payload, which the caller keeps reading.
 */
export function extractDeployHistory(
  app: ArgoApplication,
): DeployHistoryEntry[] {
  const history = app.status?.history ?? [];

  return [...history].reverse().map(entry => {
    // The project's source and commit, in either shape of the entry — not
    // the appenv repository's of a multi-source one (argoApp.ts).
    const { source, revision } = historyProjectSource(entry);
    return {
      id: entry.id,
      revision,
      deployedAt: entry.deployedAt,
      // Same `.git` strip as `toEnvironment`, for the same reason: the value
      // is a clone URL and every link built from it is a browsable one.
      repoUrl: source?.repoURL?.replace(/\.git$/, ''),
    };
  });
}

/**
 * The fields a timestamp is shown with: day, month and year as numbers, hours
 * and minutes — `01/10/2026 00:00` in French. No seconds: every timestamp of
 * this module is read to know *when*, not to order events within a minute.
 */
const TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * An ArgoCD timestamp, rendered for a human, in the browser's time zone.
 *
 * The locale is passed in rather than left to the browser: the two differ as
 * soon as someone reads the French UI inside an en-US browser, and a bare
 * `toLocaleString()` would then print `8/11/2026, 11:55:00 AM` in the middle of
 * a French page. Callers pass the `format.locale` message of the
 * waadoo-project-environments translation ref, NOT the app language: the French
 * messages are installed as overrides of the default language, so the app
 * language stays `en` while the page reads in French — see `format.locale` in
 * ./translation.
 *
 * An unparseable value is returned as it arrived. It comes straight from the
 * ArgoCD API, and showing what was received says more than the browser's
 * "Invalid Date" — which is also what the caller would otherwise render.
 */
export function formatTimestamp(
  value: string | undefined,
  locale: string,
  absent = '—',
): string {
  if (!value) {
    return absent;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, TIMESTAMP_FORMAT).format(date);
}

/**
 * The first seven characters of a commit SHA, the length git itself abbreviates
 * to. Returns undefined rather than an empty string when there is no revision,
 * so the caller decides what an absent commit looks like instead of rendering a
 * blank cell.
 */
export function shortRevision(
  revision: string | undefined,
): string | undefined {
  if (!revision) {
    return undefined;
  }
  return revision.slice(0, 7);
}

/**
 * The forge page for one commit. The two forges spell it differently, and the
 * choice is made by {@link repoForge} rather than by a second hostname test, so
 * that a history row can never link to a forge the repository does not live on.
 */
export function revisionUrl(repoUrl: string, revision: string): string {
  if (repoForge(repoUrl) === 'github') {
    return `${repoUrl}/commit/${encodeURIComponent(revision)}`;
  }
  return `${repoUrl}/-/commit/${encodeURIComponent(revision)}`;
}
